import { useRef, useCallback, useEffect } from 'react';
import { type ApolloError } from 'apollo-client';
import { type OperationVariables } from '@apollo/react-common';
import { useLazyQuery, type QueryLazyOptions } from '@apollo/react-hooks';

export function useLazyQueryPromiseInterop<TData, TVariables = OperationVariables>(
	...args: Parameters<typeof useLazyQuery<TData, TVariables>>
): (options?: QueryLazyOptions<TVariables>) => Promise<TData> {
	const resolversRef = useRef<{ resolve(data: TData): void; reject(error: ApolloError): void }>();

	/**
	 * NOTE: Using `onCompleted` and `onError` callbacks does not work as expected due to Apollo bug which is present in both v2 and v3 - https://github.com/apollographql/apollo-client/issues/9338
	 */
	const [doQuery, { data, error }] = useLazyQuery<TData, TVariables>(...args);

	useEffect(() => {
		if (data) resolversRef.current?.resolve?.(data);
		if (error) resolversRef.current?.reject?.(error);
	}, [data, error]);

	return useCallback(
		(vars?: QueryLazyOptions<TVariables>) => {
			let resolve: (data: TData) => void;
			let reject: (error: ApolloError) => void;
			const promise = new Promise<TData>((res, rej) => {
				resolve = res;
				reject = rej;
			});

			// @ts-expect-error `resolve` and `reject` *will* be assigned via the Promise constructor, but TS can't guarantee that. This should be replaced with `Promise.withResolvers` once out jest setup properly supports that
			resolversRef.current = { resolve, reject };

			doQuery(vars);

			return promise;
		},
		[doQuery],
	);
}
